import { FormikValues } from 'formik'
import { useEffect, KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as Yup from 'yup'

import { Button } from 'components/button'
import { Form } from 'components/form'
import { PasswordInput } from 'components/passwordInput'
import { TextInput } from 'components/textInput'

import { useQuery } from 'hooks/useQuery'

import { browserHistory } from 'navigation/browserHistory'
import { DASHBOARD_PAGE } from 'navigation/paths'

import { GlobalState } from 'store'
import { login } from 'store/login/actions'
import { openModal } from 'store/modal/actions'

import { ModalName } from 'types/enums/ModalName'

import { ForgetPasswordModal } from './components/forgetPasswordModal'

import {
  LoginWrapper,
  ForgetPasswordLink,
  FormSection,
  HeaderForm,
  DataForm,
  HeaderImageContainer,
  LogoContainer,
  LoginTextContainer,
  PointsContainer,
  LoginTitleContainer,
  Spacer
} from './styled'
import { showError } from 'utils/alertUtils'

import { ReactComponent as LoginHeaderImage } from 'assets/images/login/login-header-image.svg'
import { ReactComponent as LoginLogo } from 'assets/images/login/login-logo.svg'
import { ReactComponent as LoginText } from 'assets/images/login/login-text.svg'
import { ReactComponent as BackgroundPoints } from 'assets/images/login/background-points.svg'
import { ReactComponent as LoginTitle } from 'assets/images/login/login-title.svg'

export function LoginPage(): JSX.Element {
  const { t } = useTranslation(['pages', 'messages'])

  const dispatch = useDispatch()

  const query = useQuery()

  const location = useLocation<{ error: string }>()

  const { loggedIn } = useSelector((state: GlobalState) => state.session)
  const { loginInProgress } = useSelector((state: GlobalState) => state.login)

  const initialValues = {
    login: '',
    password: ''
  }

  const validationSchema = Yup.object().shape({
    login: Yup.string().required('required'),
    password: Yup.string().required('required')
  })

  useEffect(() => {
    if (loggedIn) {
      if (query.get('redirectUrl')) {
        browserHistory.push(query.get('redirectUrl') || DASHBOARD_PAGE)
      } else {
        browserHistory.push(DASHBOARD_PAGE)
      }
    }
  }, [loggedIn])

  useEffect(() => {
    if (location?.state?.error) {
      showError('loginSSO', { code: location.state.error })
    }
  }, [location])

  function _handleSubmit(data: FormikValues) {
    dispatch(login({ login: data.login, password: data.password }))
  }

  document.title = 'Resolução de Disputas - Open Insurance'

  return (
    <LoginWrapper>
      <PointsContainer top={true}>
        <BackgroundPoints />
      </PointsContainer>
      <PointsContainer top={false}>
        <BackgroundPoints />
      </PointsContainer>

      <FormSection>
        <HeaderForm>
          <HeaderImageContainer>
            <LoginHeaderImage />
          </HeaderImageContainer>
          <LogoContainer>
            <LoginLogo />
          </LogoContainer>
          <LoginTextContainer>
            <LoginText />
          </LoginTextContainer>
        </HeaderForm>
        <DataForm>
          <LoginTitleContainer>
            <LoginTitle />
          </LoginTitleContainer>
          <Form
            fullWidth={true}
            disableMargin={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => _handleSubmit(values)}
          >
            <TextInput
              isEmail={true}
              label="EMAIL"
              name="login"
              placeholder={t('pages:login.form.login')}
              autoComplete="username"
              onInput={(event: KeyboardEvent<HTMLInputElement>) => {
                event.currentTarget.value = event.currentTarget.value.toLowerCase()
              }}
            />

            <Spacer />

            <PasswordInput
              label="SENHA"
              name="password"
              placeholder={t('pages:login.form.password')}
              autoComplete="off"
              showStrength={false}
            />

            <ForgetPasswordLink
              type="button"
              onClick={() => dispatch(openModal(ModalName.FORGET_PASSWORD))}
            >
              {t('pages:login.forgetPassword')}
            </ForgetPasswordLink>

            <Button
              tooltip={t('pages:login.form.submitButton')}
              type="submit"
              fullWidth
              backgroundColor="primary"
              inProgress={loginInProgress}
            >
              {t('pages:login.form.submitButton')}
            </Button>
          </Form>
        </DataForm>
      </FormSection>
      <ForgetPasswordModal />
    </LoginWrapper>
  )
}
